<template>
  <AppModalEdit
    v-model="isShowing"
    :loading="loading"
    :title="title"
    :is-exec-button-disabled="
      !editedItem.agencyId ||
        !editedItem.name ||
        invalidEmailAddresses.length > 0
    "
    @clickExec="saveEditedItem"
  >
    <v-container grid-list-md>
      <v-layout wrap>
        <v-flex xs12>
          <v-text-field v-model="editedItem.name" label="広告主名（必須）" />
        </v-flex>
        <v-flex xs12>
          <SearchAgency
            v-if="value"
            v-model="editedItem.agencyId"
            :agency-list="agencyList"
            :is-edit="isEdit"
            :is-super-admin="isSuperAdmin"
          >
            <template v-if="item.agency" v-slot:fixed>
              <AppAgencyName
                :agency="item.agency"
                :with-system-owner="isSuperAdmin"
              />
            </template>
          </SearchAgency>
        </v-flex>
        <v-flex v-if="isEdit" xs12>
          <v-switch
            v-model="editedItem.status"
            true-value="active"
            false-value="inactive"
            label="ステータス"
          />
        </v-flex>
      </v-layout>
      <AdvancedSetting button-text="高度な設定">
        <v-layout>
          <v-flex xs12>
            <v-text-field
              v-model="editedItem.code"
              label="管理コード（任意）"
            />
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs12>
            <SelectTimeZone v-if="value" v-model="editedItem.timeZone" />
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs2>
            <h3 class="subheading grey--text text--darken-2">通貨</h3>
          </v-flex>
          <v-flex xs10>{{ displayCurrency }}</v-flex>
        </v-layout>
      </AdvancedSetting>
      <v-layout pt-4>
        <v-flex xs2>
          <h3 class="subheading grey--text text--darken-2">ブランド名を選択</h3>
        </v-flex>
        <v-flex xs10>
          <v-radio-group v-model="useBrand" mandatory>
            <v-radio :value="true" label="する" />
            <v-radio
              :value="false"
              @click="refreshBrandSetting"
              label="しない"
            />
          </v-radio-group>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs12>
          <SearchBrand
            v-if="value"
            v-model="brandData"
            :disabled="!useBrand"
            :defaultBrand="{
              id: editedItem.brandId,
              name: editedItem.brandName,
            }"
          />
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs12>
          <SelectCategory
            v-if="value"
            v-model="editedItem.categoryMasterId"
            :category-name="editedItem.categoryName"
            :disabled="useBrand"
          />
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs12>
          <h3 class="subheading grey--text text--darken-2">
            広告主担当者（任意）
          </h3>
          <AddableTextField
            :email-addresses="emailAddresses"
            :invalid-email-addresses="invalidEmailAddresses"
            @remove="removeConfirm"
            @inputText="onInputText"
          />
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <SelectAgencySalesAccount
          v-model="editedItem.agencySalesAccountId"
          :agency-id="editedItem.agencyId"
          :items="agencyAccountList"
          label="代理店営業担当者（任意）"
          placeholder="代理店の営業担当者を選択して下さい"
          clearable
        />
      </v-layout>
      <v-layout v-if="account.belongType === 'system owner'" row wrap>
        <SelectSalesAccount
          v-if="account.systemOwnerId"
          v-model="editedItem.systemOwnerSalesAccountId"
          :label="account.belongName + '担当者（任意）'"
          placeholder="営業担当者を選択して下さい"
          clearable
        />
      </v-layout>
    </v-container>
  </AppModalEdit>
</template>

<script>
import { mapState } from 'vuex'
import { actions, snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import { AddAdvertiserForm, EditAdvertiserForm } from '@/models/Advertiser'
import AddableTextField from '@/components/Shared/CrudUI/AddableTextField'
import SelectSalesAccount from '@/components/Shared/CrudUI/SelectSalesAccount'
import AdvancedSetting from '@/components/Shared/CrudUI/AdvancedSetting'
import SelectAgencySalesAccount from './EditModalSelectAgencySalesAccount'
import SearchAgency from './EditModalSearchAgency'
import SearchBrand from './EditModalSearchBrand'
import SelectCategory from './EditModalSelectCategory'
import SelectTimeZone from './EditModalSelectTimeZone'
import AgencyApi from '@/api/agency'
export default {
  components: {
    AdvancedSetting,
    AddableTextField,
    SearchAgency,
    SearchBrand,
    SelectCategory,
    SelectAgencySalesAccount,
    SelectSalesAccount,
    SelectTimeZone,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: () => {},
    },
    agencyList: {
      type: Array,
      default: () => [],
    },
    agencyAccountList: {
      type: Array,
      default: () => [],
    },
    isSuperAdmin: Boolean,
  },
  data() {
    return {
      editedItem: {},
      emailAddresses: [],
      invalidEmailAddresses: [],
      errorEmailAddresses: [],
      searchBrandKeyword: '',
      selectedBrand: {},
      loading: false,
      loadingBrandList: false,
      loadingOfficerList: false,
      useBrand: false,
      canShowRemoveMailMessage: true,
      displayEmailAddressesErrorModal: false,
    }
  },
  computed: {
    ...mapState({
      account: state => state.account.account,
    }),
    title() {
      return this.isEdit ? '広告主編集' : '広告主登録'
    },
    displayCurrency() {
      if (this.editedItem.currency === 'JPY') {
        return '日本円（JPY）'
      }
      return this.editedItem.currency
    },
    isShowing: {
      get() {
        return this.value
      },
      set(isDisplay) {
        return this.$emit('input', isDisplay)
      },
    },
    brandData: {
      get() {
        return this.editedItem.brandId
      },
      set(value) {
        this.editedItem.brandId = value.id
        this.editedItem.brandName = value.name
        this.editedItem.categoryMasterId = value.categoryId
        this.editedItem.categoryName = value.categoryName
      },
    },
    canSetCategory() {
      // ブランドが設定されているときは業種の設定不可
      if (this.editedItem == null) return false
      return Boolean(this.editedItem.brandId)
    },
    emailAddressesEmptyFiltered() {
      const emptyResult = this.isEdit ? null : undefined
      if (this.emailAddresses == null) return emptyResult
      const result = this.emailAddresses.filter(e => e.length !== 0)
      // 何も入力されてなかったらundefined（create）かnull（edit）を送る
      return result.length === 0 ? emptyResult : result
    },
  },
  watch: {
    value(newVal) {
      this.$store.dispatch('master/CLEAR_BRAND_LIST')
      this.$store.dispatch('admin/advertiser/CLEAR_AGENCY_LIST')
      if (newVal) this.init()
    },
    async 'editedItem.agencyId'() {
      if (this.isEdit) return
      const agency = await AgencyApi.get(this.editedItem.agencyId)
      this.editedItem.systemOwnerSalesAccountId = agency.item.salesAccountId
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.emailAddresses = []
      this.searchBrandKeyword = ''
      this.selectedBrand = {}
      this.canShowRemoveMailMessage = true
      if (this.isEdit) {
        this.initOnEdit()
      } else {
        this.initOnCreate()
      }
      // editであっても、nullであれば初期値をセット
      if (this.editedItem.currency == null) this.editedItem.currency = 'JPY'
      if (this.editedItem.timeZone == null)
        this.editedItem.timeZone = 'Asia/Tokyo'
    },
    initOnCreate() {
      this.useBrand = false
      this.editedItem = new AddAdvertiserForm()
      this.editedItem.status = 'active'
    },
    initOnEdit() {
      this.item.fetchAgency()
      this.useBrand = Boolean(this.item.brandId)
      // copy
      this.editedItem = new EditAdvertiserForm(this.item)
      this.emailAddresses = this.editedItem.emailAddresses
    },
    refreshBrandSetting() {
      this.editedItem.brandId = undefined
      this.editedItem.brandName = undefined
      this.editedItem.categoryMasterId = undefined
      this.editedItem.categoryName = undefined
      this.useBrand = false
    },
    // 入力がきたら、バリデーションも一緒にかける
    onInputText(email) {
      this.emailAddresses[email.index] = email.text
      this.filterInvalidEmailAddresses()
    },
    filterInvalidEmailAddresses() {
      const validateEmailRegex = /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/
      this.invalidEmailAddresses = this.emailAddresses.filter(email => {
        return !validateEmailRegex.test(email) && email.length > 0
      })
    },
    async removeConfirm(email) {
      if (email.length === 0) return
      if (!this.canShowRemoveMailMessage) return
      await this.$confirm('注意', {
        okText: '閉じる',
        onlyOk: true,
        body:
          'アカウントを管理者から外しても、\n自動的に管理者アカウントが外れることはありません。\nアカウント管理から設定してください。',
      })
      this.canShowRemoveMailMessage = false
    },
    async saveEditedItem() {
      this.loading = true
      try {
        this.editedItem.emailAddresses = this.emailAddressesEmptyFiltered
        const advertiser = this.isEdit
          ? await this.editedItem.editAdvertiser(this.item.id)
          : await this.editedItem.addAdvertiser()
        await this.$store.dispatch(actions.SHOW_SNACKBAR, {
          type: snackbarType.SUCCESS,
          message: '保存完了しました',
        })
        // saveをemitするとpageコンポーネントでgetlistが再度走る
        this.$emit('save', advertiser.emailAddresses.errors)
      } catch (e) {
        this.loading = false
        if (
          e.message.startsWith(
            '入力された広告主担当者のメールアドレスは既に招待されていますが、未登録です。',
          )
        ) {
          await this.$confirm('', {
            okText: '閉じる',
            onlyOk: true,
            body: e.message,
          })
          return
        } else {
          throw e
        }
      }
      this.isShowing = false
      this.loading = false
    },
  },
}
</script>
